import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from 'react-avatar';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import ElipsisText from '../../../../../components/ElipsisText';
import ElipsisCell from '../../../../../components/TableBox/Cells/EllipsisCell';
import Link from '../../../../../components/Link';
import Badge from '../../../../../components/Badge';
import Button from '../../../../../components/Button';
import {
  RowClassName,
  PermissionModulesEnum,
  PermissionActionsEnum,
} from '../../../../../enums';
import { TableContext } from '../../../../../contexts/TableContext';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { hasResourseAccess } from '../../../../../utils/methods';
import { StockistBranchResponse } from '../../../../../interfaces/stockist.interface';

interface RowProps {
  item: StockistBranchResponse;
  className?: RowClassName;
}

const BranchRow: React.FC<RowProps> = ({ item, className }) => {
  const { t } = useTranslation();
  const { headCells, rowProps } = useContext(TableContext);
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Stockists]: [PermissionActionsEnum.Update] }],
  });

  const handleEditButtonClick = (id: string | number) => {
    if (rowProps?.onEdit) {
      rowProps.onEdit(id);
    }
  };

  // For now view click also opens edit? Or we can add onView logic later
  const handleViewClick = (id: string | number) => {
    if (rowProps?.onEdit) {
      rowProps.onEdit(id);
    }
  };

  const nameNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 42 }}>
        <Avatar name={item.name || '-'} size="40" maxInitials={2} round />
      </div>
      <div style={{ width: 'calc(100% - 52px)' }}>
        <ElipsisText
          width="100%"
          text={item.name || '-'}
          node={<Link text={item.name || '-'} />}
          className="h6 mb-1 text-decoration-underline hyperlink"
          onClick={() => handleViewClick(item.id)}
        />
        <ElipsisText width="100%" text={item.code || ''} />
      </div>
    </div>
  );

  const updatedNode = (
    <div>
      <div className="d-block text-truncate" style={{ maxWidth: '150px' }}>
        {item.modify_by?.first_name} {item.modify_by?.last_name}
      </div>
      <ElipsisText width="100%" text={item.modify_date ? new Date(item.modify_date).toLocaleDateString() : '-'} />
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.name) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={nameNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.city) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.city?.value || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.state) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.state?.value || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.email) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.email || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={
                  item.is_active
                    ? t('stockists.stockistBranch.list.pageTexts.table.status.active') || 'Active'
                    : t('stockists.stockistBranch.list.pageTexts.table.status.inActive') || 'Inactive'
                }
                variant={item.is_active ? 'success' : 'danger'}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.updated_at) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={updatedNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-sm-center justify-content-sm-start">
                <Button
                  className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                  icon={<i className={`ri-${hasEditAccess ? 'edit-box' : 'eye'}-line ri-20px`}></i>}
                  onClick={() => handleEditButtonClick(item.id)}
                />
              </div>
            </td>
          );
        }

        const value = item[headCell.id as keyof StockistBranchResponse];
        return (
          <ElipsisCell key={headCell.id} width={headCell.width} text={value ? String(value) : '-'} />
        );
      })}
    </tr>
  );
};

export default BranchRow;
